/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { SpecialInstruction } from './SpecialInstruction';
import {
    SpecialInstructionFromJSON,
    SpecialInstructionFromJSONTyped,
    SpecialInstructionToJSON,
    SpecialInstructionToJSONTyped,
} from './SpecialInstruction';
import type { SpecialCharacteristic } from './SpecialCharacteristic';
import {
    SpecialCharacteristicFromJSON,
    SpecialCharacteristicFromJSONTyped,
    SpecialCharacteristicToJSON,
    SpecialCharacteristicToJSONTyped,
} from './SpecialCharacteristic';
import type { Party } from './Party';
import {
    PartyFromJSON,
    PartyFromJSONTyped,
    PartyToJSON,
    PartyToJSONTyped,
} from './Party';
import type { ExternalReference } from './ExternalReference';
import {
    ExternalReferenceFromJSON,
    ExternalReferenceFromJSONTyped,
    ExternalReferenceToJSON,
    ExternalReferenceToJSONTyped,
} from './ExternalReference';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
    DocumentToJSONTyped,
} from './Document';
import type { CustomerOrderLine } from './CustomerOrderLine';
import {
    CustomerOrderLineFromJSON,
    CustomerOrderLineFromJSONTyped,
    CustomerOrderLineToJSON,
    CustomerOrderLineToJSONTyped,
} from './CustomerOrderLine';
import type { Link } from './Link';
import {
    LinkFromJSON,
    LinkFromJSONTyped,
    LinkToJSON,
    LinkToJSONTyped,
} from './Link';
import type { Contact } from './Contact';
import {
    ContactFromJSON,
    ContactFromJSONTyped,
    ContactToJSON,
    ContactToJSONTyped,
} from './Contact';

/**
 * Response schema for CustomerOrder logistics object.
 * @export
 * @interface CustomerOrderResponse
 */
export interface CustomerOrderResponse {
    /**
     * Creation date of the customer order in the sale or purchase process.
     * @type {Date}
     * @memberof CustomerOrderResponse
     */
    creationDate?: Date;
    /**
     * Party buying the goods.
     * @type {Party}
     * @memberof CustomerOrderResponse
     */
    buyer?: Party;
    /**
     * A customer order can be delivered at once or partially. Each consignment represents the group of goods to be delivered from an origin location to a delivery location.
     * @type {Array<Link>}
     * @memberof CustomerOrderResponse
     */
    consignments?: Array<Link>;
    /**
     * The contacts of the customer order refer to the list of individuals or teams that can be contacted regarding the customer order.
     * @type {Array<Contact>}
     * @memberof CustomerOrderResponse
     */
    contacts?: Array<Contact>;
    /**
     * List of the detailed lines of the products ordered by the customer.
     * @type {Array<CustomerOrderLine>}
     * @memberof CustomerOrderResponse
     */
    customerOrderLines?: Array<CustomerOrderLine>;
    /**
     * List of  documents related to the customer order, such as signed orders.
     * @type {Array<Document>}
     * @memberof CustomerOrderResponse
     */
    documents?: Array<Document>;
    /**
     * External references of the customer order should be used when they are defined in a legacy system that registers the customer order under a different reference identifier than the URI. This is useful for searching customer orders using the ID from the legacy application. Common external reference types include 'SELLER_ID' and 'BUYER_ID'. Each party with access to the customer order can request to add their own external reference through a change request.
     * @type {Array<ExternalReference>}
     * @memberof CustomerOrderResponse
     */
    externalReferences?: Array<ExternalReference>;
    /**
     * Seller is the party selling the products to the buyer
     * @type {Party}
     * @memberof CustomerOrderResponse
     */
    seller?: Party;
    /**
     * Special characteristics applied to the customer order and to be shared with a provider.
     * @type {Array<SpecialCharacteristic>}
     * @memberof CustomerOrderResponse
     */
    specialCharacteristics?: Array<SpecialCharacteristic>;
    /**
     * Special instructions applied to the customer order and to be shared with a provider.
     * @type {Array<SpecialInstruction>}
     * @memberof CustomerOrderResponse
     */
    specialInstructions?: Array<SpecialInstruction>;
    /**
     * Internal unique identifier of the resource. Id can have different formats like UUID v4, UUID v7, or any other string calculated.
     * @type {string}
     * @memberof CustomerOrderResponse
     */
    id?: string;
}

/**
 * Check if a given object implements the CustomerOrderResponse interface.
 */
export function instanceOfCustomerOrderResponse(value: object): value is CustomerOrderResponse {
    return true;
}

export function CustomerOrderResponseFromJSON(json: any): CustomerOrderResponse {
    return CustomerOrderResponseFromJSONTyped(json, false);
}

export function CustomerOrderResponseFromJSONTyped(json: any, ignoreDiscriminator: boolean): CustomerOrderResponse {
    if (json == null) {
        return json;
    }
    return {
        
        'creationDate': json['creationDate'] == null ? undefined : (new Date(json['creationDate'])),
        'buyer': json['buyer'] == null ? undefined : PartyFromJSON(json['buyer']),
        'consignments': json['consignments'] == null ? undefined : ((json['consignments'] as Array<any>).map(LinkFromJSON)),
        'contacts': json['contacts'] == null ? undefined : ((json['contacts'] as Array<any>).map(ContactFromJSON)),
        'customerOrderLines': json['customerOrderLines'] == null ? undefined : ((json['customerOrderLines'] as Array<any>).map(CustomerOrderLineFromJSON)),
        'documents': json['documents'] == null ? undefined : ((json['documents'] as Array<any>).map(DocumentFromJSON)),
        'externalReferences': json['externalReferences'] == null ? undefined : ((json['externalReferences'] as Array<any>).map(ExternalReferenceFromJSON)),
        'seller': json['seller'] == null ? undefined : PartyFromJSON(json['seller']),
        'specialCharacteristics': json['specialCharacteristics'] == null ? undefined : ((json['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicFromJSON)),
        'specialInstructions': json['specialInstructions'] == null ? undefined : ((json['specialInstructions'] as Array<any>).map(SpecialInstructionFromJSON)),
        'id': json['id'] == null ? undefined : json['id'],
    };
}

export function CustomerOrderResponseToJSON(json: any): CustomerOrderResponse {
    return CustomerOrderResponseToJSONTyped(json, false);
}

export function CustomerOrderResponseToJSONTyped(value?: CustomerOrderResponse | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'creationDate': value['creationDate'] == null ? value['creationDate'] : value['creationDate'].toISOString(),
        'buyer': PartyToJSON(value['buyer']),
        'consignments': value['consignments'] == null ? undefined : ((value['consignments'] as Array<any>).map(LinkToJSON)),
        'contacts': value['contacts'] == null ? undefined : ((value['contacts'] as Array<any>).map(ContactToJSON)),
        'customerOrderLines': value['customerOrderLines'] == null ? undefined : ((value['customerOrderLines'] as Array<any>).map(CustomerOrderLineToJSON)),
        'documents': value['documents'] == null ? undefined : ((value['documents'] as Array<any>).map(DocumentToJSON)),
        'externalReferences': value['externalReferences'] == null ? undefined : ((value['externalReferences'] as Array<any>).map(ExternalReferenceToJSON)),
        'seller': PartyToJSON(value['seller']),
        'specialCharacteristics': value['specialCharacteristics'] == null ? undefined : ((value['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicToJSON)),
        'specialInstructions': value['specialInstructions'] == null ? undefined : ((value['specialInstructions'] as Array<any>).map(SpecialInstructionToJSON)),
        'id': value['id'],
    };
}

